"use client";

import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { useInternalVolumeAccess } from "@/lib/useInternalVolumeAccess";

type Bucket = {
  bucket: string;
  observations: number;
  avg_return_pct: number | null;
  avg_excess_pct: number | null;
  hit_rate_pct: number | null;
  confidence: "established" | "emerging" | "building";
};

type Backtest = {
  ok?: boolean;
  benchmark?: string;
  sessions?: number;
  date_range?: { start: string | null; end: string | null };
  buckets?: Bucket[];
};

const confStyle = (c: Bucket["confidence"]): CSSProperties => {
  const base: CSSProperties = { display: "inline-block", padding: "0.1rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase" };
  if (c === "established") return { ...base, background: "rgba(16,185,129,0.16)", color: "#34d399", border: "1px solid rgba(16,185,129,0.35)" };
  if (c === "emerging") return { ...base, background: "rgba(96,165,250,0.14)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.32)" };
  return { ...base, background: "rgba(148,163,184,0.12)", color: "var(--color-text-muted)", border: "1px solid rgba(148,163,184,0.25)" };
};

export default function DeliveryBacktestClient() {
  const { allowed, checked, token } = useInternalVolumeAccess();
  const [d, setD] = useState<Backtest | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!checked || !allowed || !token) return;
    const ac = new AbortController();
    fetch("/api/outputs/delivery_backtest.json", {
      cache: "no-store",
      signal: ac.signal,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setD)
      .catch(() => { if (!ac.signal.aborted) setErr(true); });
    return () => ac.abort();
  }, [checked, allowed, token]);

  if (!allowed || err || !d || !d.ok || !d.buckets || d.buckets.length === 0) return null;

  return (
    <section style={{ maxWidth: "920px", margin: "0 auto", padding: "0 1rem 3rem" }}>
      <div style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(148,163,184,0.16)", borderRadius: "12px", padding: "1.25rem 1.5rem" }}>
        <h2 style={{ margin: "0 0 0.35rem", fontSize: "1.05rem", color: "#3b82f6" }}>Does delivery predict follow-through?</h2>
        <p style={{ margin: "0 0 1rem", fontSize: "0.9rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
          We bucket every stock by its UIN delivery ratio on a session and measure its <strong>next-session return</strong> vs the
          market average that day, across {d.sessions ?? 0} sessions
          {d.date_range?.start && d.date_range?.end ? ` (${d.date_range.start} → ${d.date_range.end})` : ""}.
          If high delivery (conviction) is real, the high bucket should outperform.
        </p>
        <div style={{ overflowX: "auto", borderRadius: "10px", border: "1px solid rgba(148,163,184,0.14)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ background: "rgba(59,130,246,0.08)" }}>
                {[["Delivery bucket", "left"], ["Avg next-session", "right"], ["Excess vs market", "right"], ["Beat market", "right"], ["Sample", "right"], ["Confidence", "right"]].map(([l, a]) => (
                  <th key={l} style={{ padding: "0.7rem 0.9rem", textAlign: a as "left" | "right", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)", whiteSpace: "nowrap", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.buckets.map((b, i) => {
                const exc = b.avg_excess_pct ?? 0;
                return (
                  <tr key={b.bucket} style={{ background: i % 2 ? "rgba(59,130,246,0.04)" : "transparent", borderBottom: "1px solid rgba(148,163,184,0.08)" }}>
                    <td style={{ padding: "0.6rem 0.9rem", fontWeight: 600 }}>{b.bucket}</td>
                    <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{b.avg_return_pct != null ? `${b.avg_return_pct > 0 ? "+" : ""}${b.avg_return_pct.toFixed(2)}%` : "—"}</td>
                    <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: exc > 0 ? "var(--color-success)" : exc < 0 ? "var(--color-danger)" : "var(--color-text-muted)" }}>{b.avg_excess_pct != null ? `${exc > 0 ? "+" : ""}${exc.toFixed(2)}%` : "—"}</td>
                    <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", color: "var(--color-text-muted)", fontVariantNumeric: "tabular-nums" }}>{b.hit_rate_pct != null ? `${b.hit_rate_pct.toFixed(0)}%` : "—"}</td>
                    <td style={{ padding: "0.6rem 0.9rem", textAlign: "right", color: "var(--color-text-muted)", fontVariantNumeric: "tabular-nums" }}>{b.observations.toLocaleString()}</td>
                    <td style={{ padding: "0.6rem 0.9rem", textAlign: "right" }}><span style={confStyle(b.confidence)}>{b.confidence}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ marginTop: "1rem", fontSize: "0.8rem", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
          Benchmark is the cross-sectional session mean (equal-weight), so this isolates the delivery factor from market-wide moves.
          Research and education only — past performance does not guarantee future results.
        </p>
      </div>
    </section>
  );
}
